import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import OnboardingModal from '@/components/OnboardingModal';
import TooltipGuide from '@/components/TooltipGuide';
import { useStore } from '@/store/useStore';
import { Lock } from 'lucide-react';
import { Outlet, useLocation } from "react-router-dom";
import { formatDate } from '@/lib/dateFormatter';


function SubscriptionLock() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
      <div className="max-w-md flex flex-col items-center">
        <Lock className="w-20 h-20 text-white mb-6" />
        <h1 className="text-3xl font-bold mb-4 text-white">
          Subscription Required
        </h1>
        <p className="text-gray-400 mb-6">
          Your subscription is inactive or has expired.
          Please renew to continue using all features.
        </p>
      </div>
    </div>
  );
}

const MainLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { updateShowOnboarding } = useStore();
  const showOnboardingModal = useStore((state) => state.showOnboarding);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTooltips, setShowTooltips] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [gracePeriod, setGracePeriod] = useState(false);
  const user = useStore((state) => state.user);
  const location = useLocation();


  useEffect(() => {
    setShowOnboarding(!user?.completedOnboarding);
    if (!user?.verifiedAt) {
      return;
    }

    const registrationDate = formatDate(user.verifiedAt);
    if (registrationDate) {
      const graceEnd = new Date(registrationDate.getTime() + 30 * 60 * 1000);
      const isWithin = new Date() <= graceEnd;
      setGracePeriod(isWithin);
    } else {
      setGracePeriod(false);
    }
  }, [user]);


  const handleOnboardingComplete = () => {
    updateShowOnboarding(false);
    setShowOnboarding(false);
    setHasCompletedOnboarding(true);
    setTimeout(() => {
      setShowTooltips(true);
    }, 500);
  };

  const handleCloseTooltips = () => {
    setShowTooltips(false);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);

      // Auto-close sidebar on mobile
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    // Check on initial load
    checkMobile();

    // Add listener for resize
    window.addEventListener('resize', checkMobile);

    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, [location]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="h-screen flex overflow-hidden dark:bg-black">
      {/* Sidebar */}
      <div
        className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed inset-y-0 left-0 z-30 lg:relative lg:translate-x-0 transition-transform duration-300 ease-in-out lg:flex`}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          onToggleSidebar={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          handleStartOnboarding={() => setShowOnboarding(true)}
          handleStartTooltips={() => setShowTooltips(true)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="container p-6 mx-auto max-w-6xl mb-12">
            {!user?.activeSubscriber && !gracePeriod && user?.completedOnboarding ? <SubscriptionLock /> : <Outlet />}
            {/* {children} */}
          </div>
          {/* Footer */}
          <footer className="fixed px-6 bottom-0 w-full bg-gray-100 dark:bg-black text-sm py-4 z-10">
            <div className="max-w-6xl text-gray-600 dark:text-gray-400">
              &copy; {new Date().getFullYear()} Ezyvibes. All rights reserved.
              <a href="https://ezyvibes.com/disclaimer" target="_blank" className="ml-1 text-secondary underline cursor-pointer">Health & Allergy Disclaimer</a>
            </div>
          </footer>
        </main>

      </div>

      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      {/* Modals */}
      {user?.termsAccepted && user?.verified && <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => {
          if (!user?.completedOnboarding) setShowOnboarding(false);
          updateShowOnboarding(false);
        }}
        onComplete={handleOnboardingComplete}
      />}

      {user?.termsAccepted && user?.verified && <TooltipGuide
        isActive={showTooltips}
        onClose={handleCloseTooltips}
      />}
    </div>
  );
};

export default MainLayout;